import { Button, Card } from "@/ingot";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useBusinessInfo } from "@/api/queries/businessInfo";
import { useAuthMe } from "@/api/queries/auth";
import { useDeleteCartItem } from "@/api/queries";
import {
  useUpdateCartAssemblyIntent,
  type SaveCartAsQuoteResponse,
} from "@/api/queries/cart";
import {
  useCartDeliveryLadderOptions,
  useCartShippingEstimate,
  useCheckoutPickup,
} from "@/api/queries/cart";
import { SaveAsQuoteModal } from "@/components/cart/SaveAsQuoteModal";
import type { Cart, CartItem, Process } from "@/api/types";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { OperationIconForProcess } from "@/components/cart/OperationIcon";
import CartInquiryButton from "@/components/cart/CartInquiryButton";
import { useCartLimitText } from "@/components/cart/CartLimitWarning";
import { ShippingDoesNotFitGate } from "@/components/cart/ShippingDoesNotFitGate";
import { ASSEMBLY_GATE_ENABLED } from "@/components/cart/assemblyGateSwitch";

import { Pill } from "@/components/ui/Pill";
import { SubmitBlockers, useSubmitGuard } from "@/components/ui/SubmitGuard";
import { VatDisplayToggle } from "@/components/VatDisplayToggle";
import { useDisplayMoney } from "@/lib/currency";
import { earliestDeliveryDate, formatDeliveryDate } from "@/lib/deliveryDate";
import { isMaterialPending } from "@/lib/deliveryPending";
import { minorFromPair, minorToMajor, moneyFromPair } from "@/lib/money";
import { orderSummaryTotals } from "@/lib/orderSummaryTotals";
import { checkoutPath } from "@/lib/storefrontPaths";
import { formatVatRate } from "@/lib/utils";
import { formatWeightKg } from "@/lib/weight";
import {
  defaultVatDisplayMode,
  storefrontVatDisplay,
  writeVatDisplayPreference,
  type VatDisplayMode,
} from "@/lib/vatDisplay";
import { getOperationTone, operationToneColor } from "@/lib/operationTone";

/**
 * Brána sestavy je LAZY (KAN-301). ``/embed`` je konfigurátor na cizí
 * WordPress stránce a košík se do jeho entry chunku táhne eagerně přes
 * ``router.tsx`` — statický import by tedy dialog naúčtoval každému
 * návštěvníkovi každého tenanta, i když se k pokladně nikdy nedostane.
 * Odklad nic nestojí: modál se renderuje až po kliknutí na „na
 * pokladnu", tedy nikdy při prvním vykreslení.
 */
const AssemblyGate = lazy(() =>
  import("@/components/cart/AssemblyGate").then((m) => ({
    default: m.AssemblyGate,
  })),
);

/** KAN-1048 — týž důvod pro odklad jako u brány sestav výš: většina
 *  košíků nenaceněnou položku nemá a tenhle modál nikdy neuvidí. */
const UnpricedGate = lazy(() =>
  import("@/components/cart/UnpricedGate").then((m) => ({
    default: m.UnpricedGate,
  })),
);

/**
 * Sticky right-rail cart summary (v2 redesign).
 *
 * Adds a per-operation breakdown row above the totals — each row shows
 * the operation's icon, label, count, and subtotal. Customers reading
 * the summary can therefore see "kolik mě stojí laser vs. 3D tisk" at
 * a glance instead of one flat subtotal.
 *
 * Picking a delivery tier still happens in Checkout step 2 — the cart
 * deliberately does not ask the customer to choose. What the cart does
 * show (KAN-483) is a "doprava od / termín dodání od" lower bound, so
 * the full price is not sprung on them for the first time at the
 * checkout.
 */
export function CartSummaryV2({
  cart,
  lang,
  locale,
  primaryProcessFor,
  processBySlug,
}: {
  cart: Cart;
  lang: string;
  locale: string;
  primaryProcessFor: (item: CartItem) => string;
  processBySlug: Map<string, Process>;
}): JSX.Element {
  const { t } = useTranslation("cart");
  const { format } = useDisplayMoney(locale);
  const navigate = useNavigate();
  const deleteItem = useDeleteCartItem();
  const me = useAuthMe();
  // How this shop sells. Tenant setting, not an embed concern — the
  // storefront honours it too.
  const businessInfo = useBusinessInfo().data;
  const checkoutMode = businessInfo?.checkout_mode ?? "payment";
  const segments = businessInfo?.buyer_segments ?? null;
  // KAN-1515 — účtuje tenant DPH vůbec? Neplátci nemá co nabízet přepínač
  // „S DPH | Bez DPH" (klik jím neměnil ani jeden znak) ani co slibovat
  // doložka „bez DPH", kterou čtenář čte jako „daň se připočte později".
  // Totéž pravidlo, jaké KAN-1498 uplatnil na cenovku v konfigurátoru.
  const { appliesVat } = storefrontVatDisplay(businessInfo);
  const isLoggedIn = !!me.data?.email;
  const guard = useSubmitGuard();
  const [saveResult, setSaveResult] = useState<SaveCartAsQuoteResponse | null>(
    null,
  );
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const looksLikeB2B = isLoggedIn && !!me.data?.company;
  const [vatMode, setVatMode] = useState<VatDisplayMode>(() =>
    defaultVatDisplayMode(looksLikeB2B, segments),
  );
  useEffect(() => {
    setVatMode(defaultVatDisplayMode(looksLikeB2B, segments));
  }, [looksLikeB2B, segments]);
  const onVatModeChange = (mode: VatDisplayMode): void => {
    setVatMode(mode);
    writeVatDisplayPreference(mode);
  };

  // KAN-483 — "Doprava od X, termín dodání od Y".
  //
  // The cart has no delivery address, so neither figure can be the
  // customer's actual one; both are lower bounds, priced by the BE for
  // the tenant's default delivery country through the same path the
  // checkout uses. When the BE cannot price (no countries, no covering
  // rate, cart not quotable yet) every field is null and we say so
  // rather than showing a number — an unexplained jump from cart to
  // checkout is exactly what this line exists to prevent.
  const shippingEstimate = useCartShippingEstimate();
  // KAN-2015 — obchod s osobním odběrem: těžká zásilka nekončí poptávkou.
  const pickupOffer = useCheckoutPickup({ enabled: shippingEstimate.data?.fits_nowhere === true });
  // KAN-1623 — tranzit dopravce počítá server (pracovní dny).
  const ladderOptions = useCartDeliveryLadderOptions(
    undefined,
    shippingEstimate.data?.extra_days ?? 0,
  );
  // Wire carries minor units + the code they are denominated in;
  // ``format`` wants major units of the tenant's base currency.
  const shippingFrom =
    shippingEstimate.data?.price_minor == null
      ? null
      : minorToMajor(
          shippingEstimate.data.price_minor,
          shippingEstimate.data.currency_code,
        );
  const deliveryFrom = useMemo<Date | null>(() => {
    const extraDays = shippingEstimate.data?.extra_days;
    if (extraDays == null || !ladderOptions.data?.length) return null;
    return earliestDeliveryDate(ladderOptions.data);
  }, [shippingEstimate.data?.extra_days, ladderOptions.data]);
  const deliveryPending =
    ladderOptions.data?.some((o) => o.eta_pending_estimate) ?? false;
  // KAN-1901 — chybí materiál bez dodací lhůty: lidštější věta než obecné
  // „Termín upřesníme".
  const deliveryPendingMaterial =
    ladderOptions.data?.some(isMaterialPending) ?? false;

  const lineCount = cart.items.reduce((n, i) => n + i.lines.length, 0);
  const totalQty = cart.items.reduce(
    (n, i) => n + i.lines.reduce((m, l) => m + l.quantity, 0),
    0,
  );
  const totalWeightKg = cart.total_weight_kg ?? 0;
  // ``?? 0`` — starší BE (nebo mock v testu) nemusí pole poslat.
  // KAN-606 — every summary amount comes from the (minor, whole-unit) pair,
  // minor half winning. The whole-unit half is a rounded shadow of the number
  // checkout will actually stamp, so reading it showed the customer a total
  // that was not the one they would be charged.
  const cur = cart.currency_code;
  const money = (
    minor: number | undefined,
    legacy: number | undefined,
  ): number => moneyFromPair(minor, legacy, cur);

  const orderDiscountCzk = money(
    cart.order_discount_minor,
    cart.order_discount_czk,
  );
  const orderSurchargeCzk = money(
    cart.order_surcharge_minor,
    cart.order_surcharge_czk,
  );
  // Příplatky (KAN-142) NEJSOU v ``subtotal_czk`` — ten je součet řádků.
  // ``total_czk`` je obsahuje, takže bez vlastního řádku by souhrn
  // nesouhlasil a zákazník by viděl nevysvětlený rozdíl.
  const addonsCzk = money(cart.addons_minor, cart.addons_czk);
  // KAN-738 — dorovnání minimální objednávky (už uvnitř ``total`` i základu DPH).
  const minimumOrderTopup = money(cart.minimum_order_topup_minor, undefined);
  // KAN-2055 — dorovnání minim skupin operací (řádek za pravidlo obchodu).
  const operationGroupTopups = (cart.operation_group_topups ?? []).filter(
    (row) => row.amount_minor > 0,
  );
  const subtotal = money(cart.subtotal_minor, cart.subtotal_czk);
  // KAN-2013 — spodek souhrnu (bez DPH / DPH / k úhradě) stejnou funkcí
  // jako pokladna, v minor jednotkách, aby se nezaokrouhlovalo dvakrát.
  const summaryTotals = orderSummaryTotals({
    totalMinor: minorFromPair(cart.total_minor, cart.total_czk, cur),
    vatMinor: minorFromPair(cart.vat_minor, cart.vat_czk, cur),
    vatMode,
  });
  // KAN-1428 — tvrdou hranici zákazník vidí už v košíku, ne až na
  // pokladně, kde ho zablokuje tlačítko. Hranici posílá server v minor
  // jednotkách měny košíku; KAN-1692 — i verdikt „pod hranicí" je jeho
  // (``below_min_subtotal``, tatáž funkce jako pokladna), klient ho
  // nepočítá.
  const minSubtotalMinor = cart.min_subtotal_minor ?? null;
  const belowMinSubtotal =
    minSubtotalMinor !== null && cart.below_min_subtotal === true;

  // KAN-1827 — horní hranice (částka / hmotnost / počet dílů) zákazník vidí
  // v košíku, ne až po „Odeslat objednávku". Verdikt i parametry posílá
  // server (stejné kontroly jako pokladna); text se jen vybírá podle kódu.
  const limitViolationText = useCartLimitText(cart, locale);

  // Per-operation breakdown: ``{slug → {count, qty, subtotal}}``.
  // Order matches the order in which the slug first appears in cart.items
  // — matches the order rendered in the left column, no surprise re-sort.
  //
  // KAN-1027 — dělí se podle SKUTEČNÝCH operací řádku, ne podle jeho
  // primárky. Do té doby padal celý ``line_total_czk`` do jediného
  // kbelíku (změřeno: „3D tisk 134 Kč", ačkoli 65 z toho byla navazující
  // povrchová úprava), takže operace, kterou si zákazník sám přidal a
  // která tvořila polovinu ceny, v souhrnu nebyla vidět vůbec.
  //
  // Součet zůstává přesný: Σ(unit × qty + setup) přes operace je
  // z definice ``line_total_czk`` — proto se rozpad smí sčítat do
  // téhož mezisoučtu. Řádek bez ``operation_prices`` (starší odpověď,
  // zápisová cesta mimo ``GET /cart``) padá na dosavadní chování;
  // rozdělit ho nejde a vynechat by ubralo z celku.
  const breakdown = useMemo(() => {
    const map = new Map<
      string,
      { count: number; qty: number; subtotal: number }
    >();
    const bucket = (slug: string) => {
      const entry = map.get(slug) ?? { count: 0, qty: 0, subtotal: 0 };
      map.set(slug, entry);
      return entry;
    };
    for (const item of cart.items) {
      // KAN-301 — kontejner sestavy nemá linky ani primární operaci;
      // do rozpadu podle operace nepatří, jinak by v něm svítil
      // fantomový řádek „Nezařazené 1" bez kusů a bez ceny.
      if ((item.child_ids?.length ?? 0) > 0) continue;
      const primarySlug = primaryProcessFor(item);
      // Díl se počítá do KAŽDÉ operace, kterou prochází — „1 díl · 1 ks"
      // u tisku i u lakování je pravda o témž kusu, ne dvojí započtení;
      // sčítá se jen cena, a ta je rozdělená.
      // Nenakonfigurovaná položka (žádné linky) se počítala do své
      // primárky i dosud — „Nezařazené (1 dílů · 0 ks)" je právě ona.
      // Bez tohohle seedu by z rozpadu vypadla. U položky S linkami se
      // primárka NEseeduje: ``primaryProcessFor`` umí vrátit
      // ``unassigned`` tam, kde ``operation_prices`` nese skutečný typ,
      // a vznikl by prázdný kbelík navíc.
      const touched = new Set<string>(
        item.lines.length === 0 ? [primarySlug] : [],
      );
      for (const line of item.lines) {
        const shares = line.operation_prices ?? [];
        if (shares.length === 0) {
          const entry = bucket(primarySlug);
          entry.qty += line.quantity;
          entry.subtotal += moneyFromPair(
            line.line_total_minor,
            line.line_total_czk,
            cur,
          );
          touched.add(primarySlug);
          continue;
        }
        for (const share of shares) {
          const entry = bucket(share.process_type);
          entry.qty += line.quantity;
          // Násobí se v MINOR a do major se převádí až výsledek: každý
          // krok v major zaokrouhluje znovu a zaokrouhlení se sčítají.
          entry.subtotal += minorToMajor(
            share.unit_price_minor * line.quantity + (share.setup_fee_minor ?? 0),
            cur,
          );
          touched.add(share.process_type);
        }
      }
      for (const slug of touched) bucket(slug).count += 1;
    }
    return Array.from(map.entries());
  }, [cart.items, primaryProcessFor, cur]);

  const emptyItems = useMemo(
    // KAN-301 — kontejner NEMÁ linky záměrně (cena zůstává na dětech),
    // takže bez téhle výjimky by ho dialog „nenakonfigurované soubory"
    // nabídl ke smazání — a s ním kaskádou i celou sestavu.
    () =>
      cart.items.filter(
        (it) => it.lines.length === 0 && (it.child_ids?.length ?? 0) === 0,
      ),
    [cart.items],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [unpricedGateOpen, setUnpricedGateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // KAN-1048 — rozcestník se řídí OBSAHEM košíku, ne nastavením
  // tenanta. ``business_info.checkout_mode`` zůstává tím, čím je
  // (bere tenhle obchod karty?); s nacenitelností nemá co dělat.
  const hasUnpricedLine = cart.items.some((item) =>
    item.lines.some((line) => line.unpriced_reason),
  );

  // The customer-typed order note lives in its own ``CartOrderNoteSection``
  // mounted under the operation sections in the cart's left column. The
  // summary panel intentionally doesn't duplicate the field — keeping
  // it next to the parts list matches the UX brief ("pod výrobními
  // operacemi") and avoids two textareas writing to the same column.

  const checkoutHref = checkoutPath(lang);
  // KAN-301 — brána „Budeme s díly něco dělat?" sedí NA CESTĚ na
  // pokladnu, ne jako další krok průvodce: počet kroků košíku se
  // nemění a jednodílná objednávka odejde jedním kliknutím navíc.
  // KAN-928 — otázka padne jednou za košík: jednodílný košík se neptá
  // vůbec a zapamatovaná odpověď (``cart.assembly_intent``) ji přeskočí;
  // změnit ji jde tlačítkem v souhrnu.
  const intentUpdate = useUpdateCartAssemblyIntent();
  const rememberedIntent = cart.assembly_intent ?? null;
  // KAN-1998 — dočasně vypnuto i s řádkem „Díly: …", viz assemblyGateSwitch.
  const gateApplies = ASSEMBLY_GATE_ENABLED && cart.items.length > 1;
  /** Cesta na pokladnu pro košík, ve kterém je všechno naceněné.
   *  Varianta C zadání zní „na nic se neptat" — otázky o sestavách
   *  proto zůstávají řízené počtem dílů přesně jako dosud. */
  const continueToCheckout = () => {
    if (!gateApplies || rememberedIntent) {
      navigate(checkoutHref);
      return;
    }
    setGateOpen(true);
  };
  const onCheckoutClick = () => {
    if (emptyItems.length > 0) {
      setConfirmOpen(true);
      return;
    }
    // Nenaceněná položka se řeší PŘED branou sestav: dokud v košíku je,
    // pokladna ho stejně odmítne (``cart_has_unpriced_lines``), takže
    // ptát se dřív na sestavy by byla otázka na cestě, která nikam
    // nevede.
    if (hasUnpricedLine) {
      setUnpricedGateOpen(true);
      return;
    }
    continueToCheckout();
  };
  const onDiscardEmpty = async () => {
    setBusy(true);
    try {
      await Promise.all(emptyItems.map((it) => deleteItem.mutateAsync(it.id)));
      setConfirmOpen(false);
      continueToCheckout();
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="lg:sticky lg:top-24">
      <Card padded elevation="raised" data-testid="cart-summary">
        <div className="flex items-start justify-between gap-3">
          <h2 className="mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
            {t("summary.heading", { defaultValue: "Souhrn" })}
          </h2>
          <VatDisplayToggle
            mode={vatMode}
            onChange={onVatModeChange}
            visible={isLoggedIn && appliesVat}
          />
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <Row label={t("summary.lines")} value={String(lineCount)} />
          <Row
            label={t("summary.totalQty")}
            value={t("piecesCount", {
              count: totalQty,
              formatted: totalQty.toLocaleString(locale),
              defaultValue: "{{formatted}} ks",
            })}
          />
          {totalWeightKg > 0 && (
            <Row
              label={t("summary.weight", { defaultValue: "Hmotnost" })}
              value={formatWeightKg(totalWeightKg, locale)}
            />
          )}
        </dl>

        {breakdown.length > 0 && (
          <div className="mt-4 space-y-2 border-t border-border pt-4">
            <p className="mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              {t("summary.byOperation", { defaultValue: "Podle operace" })}
            </p>
            <ul className="space-y-1.5">
              {breakdown.map(([slug, info]) => {
                const process = processBySlug.get(slug) ?? null;
                const tone = getOperationTone(operationToneColor(process));
                const label =
                  process?.display_name ??
                  (slug === "unassigned"
                    ? t("summary.unassignedShort", {
                        defaultValue: "Nezařazené",
                      })
                    : slug);
                return (
                  <li
                    key={slug}
                    className="flex flex-col gap-0.5"
                    data-testid={`cart-summary-breakdown-${slug}`}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-ink-2">
                        <span
                          aria-hidden="true"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                          style={{ background: tone.bg, color: tone.ink }}
                        >
                          <OperationIconForProcess
                            process={process}
                            slug={slug}
                            size={12}
                          />
                        </span>
                        <span>
                          {label}
                          <span className="ml-1 text-ink-3">
                            {t("summary.byOperationLine", {
                              defaultValue: "({{count}} dílů · {{qty}} ks)",
                              count: info.count,
                              qty: info.qty,
                            })}
                          </span>
                        </span>
                      </span>
                      <span className="mono tabular-nums text-ink">
                        {format(info.subtotal)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-5 space-y-2 border-t border-border pt-4">
          {/* Sleva/přirážka JSOU už započtené v ``subtotal_czk``. Bez
              vlastních řádků se sleva projeví jen tím, že mezisoučet je
              menší, než součet položek — zákazník netuší, že nějakou
              dostal. Řádky se ukazují jen když padly (0 = nic). */}
          {orderDiscountCzk > 0 && (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-order-discount"
            >
              <span className="text-ok">
                {t("summary.orderDiscount", {
                  defaultValue: "Sleva z objednávky",
                })}
              </span>
              <span className="mono tabular-nums text-ok">
                −{format(orderDiscountCzk)}
              </span>
            </div>
          )}
          {orderSurchargeCzk > 0 && (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-order-surcharge"
            >
              <span className="text-ink-3">
                {t("summary.orderSurcharge", {
                  defaultValue: "Přirážka k objednávce",
                })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(orderSurchargeCzk)}
              </span>
            </div>
          )}
          <div
            className="flex items-baseline justify-between text-sm"
            data-testid="cart-summary-subtotal"
          >
            <span className="text-ink-3">
              {appliesVat
                ? t("summary.subtotalNet", {
                    defaultValue: "Mezisoučet bez DPH",
                  })
                : t("summary.subtotal", { defaultValue: "Mezisoučet" })}
            </span>
            <span className="mono tabular-nums text-ink">
              {format(subtotal)}
            </span>
          </div>
          {addonsCzk > 0 && (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-addons"
            >
              <span className="text-ink-3">
                {t("summary.addons", { defaultValue: "Příplatky" })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(addonsCzk)}
              </span>
            </div>
          )}
          {/* KAN-2055 — dorovnání minim skupin operací, řádek za pravidlo.
              Stejně jako dorovnání minima je UŽ v ``total`` i základu DPH. */}
          {operationGroupTopups.map((row) => (
            <div
              key={row.name}
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-operation-group-topup"
            >
              <span className="text-ink-3">
                {/* KAN-2078 — příplatek za zakázku: jméno nese pravidlo i volbu. */}
                {row.kind === "order_surcharge"
                  ? row.name
                  : t("summary.operationGroupTopup", {
                      name: row.name,
                      defaultValue: "Dorovnání minima — {{name}}",
                    })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(money(row.amount_minor, undefined))}
              </span>
            </div>
          ))}
          {/* KAN-738 — dorovnání minimální objednávky. ``total_czk`` i základ
              DPH ho UŽ obsahují; bez vlastního řádku zákazník vidí
              „Mezisoučet 20 / DPH 105 / Celkem 605" a rozdíl nemá vysvětlení. */}
          {minimumOrderTopup > 0 && (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-min-order-topup"
            >
              <span className="text-ink-3">
                {t("summary.minimumOrderTopup", {
                  defaultValue: "Dorovnání minimální objednávky",
                })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(minimumOrderTopup)}
              </span>
            </div>
          )}
          {/* KAN-2013 — spodek souhrnu bere tytéž řádky jako pokladna
              (``orderSummaryTotals``): „Celkem bez DPH" = celkem − DPH
              ze serveru, takže v něm je i dorovnání minima a příplatky.
              Úhrn je vždy částka k úhradě (s DPH). */}
          {summaryTotals.showNetRow && (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-total-net"
            >
              <span className="text-ink-3">
                {t("summary.totalNet", { defaultValue: "Celkem bez DPH" })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(minorToMajor(summaryTotals.netMinor, cur))}
              </span>
            </div>
          )}
          {summaryTotals.hasVat ? (
            <div
              className="flex items-baseline justify-between text-sm"
              data-testid="cart-summary-vat"
            >
              <span className="text-ink-3">
                {t("summary.vat", {
                  defaultValue: "DPH {{rate}} % ({{country}})",
                  rate: formatVatRate(cart.vat_rate_pct),
                  country: cart.vat_country_code ?? "",
                })}
              </span>
              <span className="mono tabular-nums text-ink">
                {format(minorToMajor(summaryTotals.vatMinor, cur))}
              </span>
            </div>
          ) : null}
          <div
            className="flex items-end justify-between pt-2"
            data-testid="cart-summary-total"
          >
            <span className="text-sm text-ink-3">
              {summaryTotals.hasVat
                ? t("summary.totalGross", { defaultValue: "Celkem s DPH" })
                : // KAN-2189 — bez DPH je úhrn „Celkem", ne druhý „Mezisoučet"
                  // pod řádkem mezisoučtu (test20 ho viděl dvakrát pod sebou).
                  t("total")}
            </span>
            <span className="mono text-2xl font-semibold tabular-nums text-ink">
              {format(minorToMajor(summaryTotals.grossMinor, cur))}
            </span>
          </div>
        </div>
        {/* Nothing at all until the estimate has actually answered.
            Rendering the "calculated at checkout" fallback while the
            query is still in flight would flash it on every cart load,
            and a sentence that shows up every time is a sentence
            nobody reads by the time it means something. */}
        {shippingEstimate.isSuccess && (
          <div
            className="mt-3 border-t border-border pt-3 text-xs text-ink-3"
            data-testid="cart-shipping-from"
          >
            {shippingEstimate.data?.fits_nowhere ? (
              // KAN-1754 — no shipping band takes the parts: no "from"
              // price, one step to a price enquiry instead.
              // KAN-2015 — má-li obchod osobní odběr, košík ho nabídne
              // (pokladna ho pak dá za 0 Kč); dřív mlčel a posílal
              // zákazníka jen do poptávky.
              <ShippingDoesNotFitGate
                lang={lang}
                defaultEmail={me.data?.email}
                testId="cart-shipping-does-not-fit"
                pickupAvailable={Boolean(pickupOffer.data)}
                inCart
              />
            ) : shippingFrom != null ? (
              <>
                <div className="flex items-baseline justify-between">
                  <span>
                    {t("summary.shippingFrom", {
                      defaultValue: "Doprava od",
                    })}
                  </span>
                  <span className="mono tabular-nums text-ink-2">
                    {format(shippingFrom)}
                  </span>
                </div>
                {deliveryFrom && (
                  <div className="mt-1 flex items-baseline justify-between">
                    <span>
                      {t("summary.deliveryFrom", {
                        defaultValue: "Termín dodání od",
                      })}
                    </span>
                    <span className="mono tabular-nums text-ink-2">
                      {formatDeliveryDate(deliveryFrom, locale)}
                    </span>
                  </div>
                )}
                {/* KAN-1438 — krok bez odhadu doby: detail po platbě
                    řekne „Termín upřesníme“, košík nesmí slíbit datum. */}
                {deliveryPending && (
                  <div
                    className="mt-1 flex items-baseline justify-between"
                    data-testid="cart-delivery-pending"
                  >
                    <span>{t("summary.deliveryPendingLabel")}</span>
                    <span className="text-ink-2">
                      {t(
                        deliveryPendingMaterial
                          ? "summary.deliveryPendingMaterial"
                          : "summary.deliveryPending",
                      )}
                    </span>
                  </div>
                )}
                <p className="mt-1 text-[11px] leading-snug text-ink-3">
                  {t("summary.shippingFromNote", {
                    defaultValue:
                      "Přesnou cenu dopravy a termín spočítáme na pokladně podle země doručení.",
                  })}
                </p>
              </>
            ) : (
              <p className="leading-snug">
                {t("summary.shippingAtCheckout", {
                  defaultValue:
                    "Dopravu spočítáme na pokladně podle země doručení.",
                })}
              </p>
            )}
          </div>
        )}
        {belowMinSubtotal && (
          <p
            className="mt-3 text-sm text-warn"
            data-testid="cart-min-subtotal-warning"
          >
            {t("summary.belowMinSubtotal", {
              subtotal: format(subtotal),
              min_subtotal: format(money(minSubtotalMinor, undefined)),
            })}
          </p>
        )}
        {limitViolationText !== null && (
          <p
            className="mt-3 text-sm text-warn"
            data-testid="cart-limit-warning"
          >
            {limitViolationText}
          </p>
        )}
        <Pill tone="ok" className="mt-4">
          {t("summary.savedNote", {
            defaultValue: "Změny v košíku se ukládají automaticky",
          })}
        </Pill>
        {checkoutMode !== "inquiry" && (
          <Button
            variant="accent"
            size="lg"
            className="mt-4 w-full"
            onClick={onCheckoutClick}
            data-testid="to-checkout"
          >
            {t("toCheckout")}
          </Button>
        )}
        {checkoutMode !== "payment" && (
          <CartInquiryButton
            variant={checkoutMode === "inquiry" ? "primary" : "secondary"}
          />
        )}
        <button
          type="button"
          className="mt-2 w-full rounded-md border border-border bg-surface px-3 py-2 text-xs text-ink-2 hover:bg-bg disabled:opacity-50"
          title={t("summary.saveAsQuoteTooltip", {
            defaultValue:
              "Uložit aktuální košík jako nabídku pro pozdější objednání.",
          })}
          onClick={() => {
            if (
              !guard.check(
                !isLoggedIn &&
                  t("summary.saveAsQuoteLoginRequired", {
                    defaultValue: "Pro uložení nabídky se prosím přihlaste.",
                  }),
                cart.items.length === 0 &&
                  t("summary.saveAsQuoteEmptyCart", {
                    defaultValue:
                      "Košík je prázdný — nejdřív do něj přidejte díly.",
                  }),
              )
            ) {
              return;
            }
            setSaveModalOpen(true);
          }}
          data-testid="save-as-quote"
        >
          {t("summary.saveAsQuote", { defaultValue: "↓ Uložit jako nabídku" })}
        </button>
        <SubmitBlockers blockers={guard.blockers} />
        {saveResult && (
          <p
            className="mt-2 rounded-md border border-ok-border bg-ok-bg px-2 py-1.5 text-[11px] text-ok"
            data-testid="save-as-quote-success"
          >
            {t("summary.saveAsQuoteSavedPrefix", {
              defaultValue: "Uloženo jako",
            })}{" "}
            <a
              href={`/${lang}/quotes/by-id/${saveResult.public_id}`}
              className="font-medium underline"
            >
              {saveResult.name ??
                t("summary.saveAsQuoteOpen", { defaultValue: "otevřít" })}
            </a>
          </p>
        )}
        <SaveAsQuoteModal
          open={saveModalOpen}
          // KAN-1652/1673 — jen nápověda v prázdném poli, neodesílá se.
          defaultName={t("summary.saveAsQuoteDefaultNameFallback")}
          quoteValidityDays={cart.quote_validity_days}
          onClose={() => setSaveModalOpen(false)}
          onSaved={setSaveResult}
        />
        <p className="mono mt-2 text-center text-[10.5px] text-ink-4">
          {t("summary.validity", { count: cart.quote_validity_days })}
        </p>
      </Card>
      <ConfirmDialog
        open={confirmOpen}
        title={t("summary.emptyDialog.title", {
          defaultValue: "Některé soubory nejsou nakonfigurované",
        })}
        description={
          <>
            <p>
              {t("summary.emptyDialog.description", {
                defaultValue:
                  "Tyto soubory nemají žádnou konfiguraci a nemohou být součástí objednávky:",
              })}
            </p>
            <ul className="mt-2 list-disc pl-5 font-mono text-xs">
              {emptyItems.map((it) => (
                <li key={it.id}>{it.file_name}</li>
              ))}
            </ul>
          </>
        }
        confirmLabel={t("summary.emptyDialog.discard", {
          defaultValue: "Zahodit a pokračovat",
        })}
        cancelLabel={t("summary.emptyDialog.back", {
          defaultValue: "Zpět ke konfiguraci",
        })}
        busy={busy}
        onConfirm={onDiscardEmpty}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        testId="cart-empty-confirm"
      />
      {gateApplies && rememberedIntent && (
        <p
          className="mt-2 flex items-center justify-between gap-2 text-[12px] text-ink-3"
          data-testid="cart-assembly-intent"
        >
          <span>
            {t("assembly.intent.label")}:{" "}
            <span className="text-ink">
              {t(`assembly.intent.${rememberedIntent}`)}
            </span>
          </span>
          <button
            type="button"
            className="underline hover:text-ink"
            onClick={() => setGateOpen(true)}
            data-testid="cart-assembly-intent-change"
          >
            {t("assembly.intent.change")}
          </button>
        </p>
      )}
      {unpricedGateOpen && (
        <Suspense fallback={null}>
          <UnpricedGate
            open={unpricedGateOpen}
            items={cart.items}
            locale={locale}
            lang={lang}
            defaultEmail={me.data?.email}
            onClose={() => setUnpricedGateOpen(false)}
            onContinueWithoutUnpriced={() => {
              // Zákazník zvolil A2 a nenaceněné řádky jsou pryč. Odsud
              // pokračuje PŮVODNÍ tok — včetně brány sestav, má-li se
              // ptát. Není to druhý modál nad touž otázkou; je to
              // otázka, na kterou se ještě nikdo neptal.
              setUnpricedGateOpen(false);
              continueToCheckout();
            }}
          />
        </Suspense>
      )}
      {gateOpen && (
        <Suspense fallback={null}>
          <AssemblyGate
            open={gateOpen}
            items={cart.items}
            locale={locale}
            lang={lang}
            defaultEmail={me.data?.email}
            onClose={() => setGateOpen(false)}
            onSkip={() => {
              setGateOpen(false);
              navigate(checkoutHref);
            }}
            onComposed={() => setGateOpen(false)}
            onIntent={(intent) => intentUpdate.mutate(intent)}
          />
        </Suspense>
      )}
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-ink-3">{label}</dt>
      <dd className="mono tabular-nums text-ink">{value}</dd>
    </div>
  );
}
